// @ts-nocheck
class MetaobjectsList extends HTMLElement {
  constructor() {
    super();
    this.metaobjectType = this.dataset.metaobjectType || '';
    this.itemsLimit = parseInt(this.dataset.itemsLimit || '50') || 50;
    this.layout = this.dataset.layout || 'grid';
    
    // Store all metaobjects and filtered results
    this.allMetaobjects = [];
    this.filteredMetaobjects = [];
    this.currentSearchTerm = '';
    this.activeFilters = {};
    

  }

  connectedCallback() {
    if (this.metaobjectType) {
      this.loadMetaobjects();
    } else {
      this.showError('Please specify a metaobject type in the section settings.');
    }
  }

  async loadMetaobjects() {
    try {
      this.showLoading();
      this.updateLoadingProgress('Starting to fetch metaobjects...');
      
      // Fetch ALL metaobjects using pagination
      const allMetaobjects = await this.fetchAllMetaobjects();
      
      if (allMetaobjects && allMetaobjects.length > 0) {
        this.allMetaobjects = allMetaobjects;
        this.filteredMetaobjects = [...allMetaobjects]; // Initially show all
        
        this.updateLoadingProgress(`Loaded ${allMetaobjects.length} locations!`);
        
        // Initialize filters and search, then render
        this.initializeFiltersAndSearch();
        this.renderMetaobjects(this.filteredMetaobjects);
      } else {
        this.showError(`No ${this.metaobjectType} metaobjects found.`);
      }
    } catch (error) {
      console.error('Error loading metaobjects:', error);
      this.showError(`Failed to load ${this.metaobjectType} metaobjects: ${error.message}`);
    }
  }

  async fetchAllMetaobjects() {
    let allMetaobjects = [];
    let hasNextPage = true;
    let cursor = null;
    let pageCount = 0;
    const METAOBJECTS_QUERY = `
      query getMetaobjects($type: String!, $first: Int!, $after: String) {
        metaobjects(type: $type, first: $first, after: $after) {
          edges {
            node {
              id
              handle
              type
              field(key: "name") { value }
              fields { key value }
            }
            cursor
          }
          pageInfo { hasNextPage endCursor }
        }
      }
    `;

    while (hasNextPage) {
      try {
        pageCount++;
        this.updateLoadingProgress(`Fetching page ${pageCount}... (${allMetaobjects.length} loaded so far)`);

        const response = await fetch('/api/2025-01/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': '20520c634c7496ac646abfeae69e7333'
          },
          body: JSON.stringify({
            query: METAOBJECTS_QUERY,
            variables: { type: this.metaobjectType, first: 250, after: cursor }
          })
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);

        const data = await response.json();
        if (data.errors) throw new Error(`GraphQL error: ${JSON.stringify(data.errors)}`);

        const edges = data.data?.metaobjects?.edges || [];
        allMetaobjects = allMetaobjects.concat(edges.map(e => e.node));
        hasNextPage = data.data?.metaobjects?.pageInfo?.hasNextPage || false;
        cursor = data.data?.metaobjects?.pageInfo?.endCursor || null;

      } catch (error) {
        console.error(`Error fetching page ${pageCount}:`, error);
        throw new Error(`Failed to fetch metaobjects page ${pageCount}: ${error.message}`);
      }
    }
    return allMetaobjects;
  }

  renderMetaobjects(metaobjects) {
    const container = this.querySelector('.metaobjects-grid');
    const loadingEl = this.querySelector('.metaobjects-loading');
    const errorEl = this.querySelector('.metaobjects-error');

    // Hide loading and error states
    if (loadingEl) loadingEl.style.display = 'none';
    if (errorEl) errorEl.style.display = 'none';

    // Clear existing content
    if (container) container.innerHTML = '';

    // Render each metaobject
    metaobjects.forEach(metaobject => {
      const itemEl = this.createMetaobjectItem(metaobject);
      if (container) container.appendChild(itemEl);
    });
  }

  createMetaobjectItem(metaobject) {
    const item = document.createElement('div');
    item.className = 'metaobject-item';
    
    let html = '';
    
    // Use the name field from the GraphQL query (matching your working pattern)
    const titleValue = metaobject.field?.value || metaobject.handle.replace(/_/g, ' ').replace(/-/g, ' ');
    html += `<h3>${this.escapeHtml(titleValue)}</h3>`;

    // Handle address fields specially - combine street, identifier, and address
    const addressField = metaobject.fields.find(f => f.key === 'address');
    const streetField = metaobject.fields.find(f => f.key === 'street');
    const identifierField = metaobject.fields.find(f => f.key === 'identifier');
    
    if (addressField || streetField) {
      html += `<div class="metaobject-field">`;
      html += `<div class="metaobject-field-label">Address:</div>`;
      html += `<div class="metaobject-field-value">`;
      html += `<div class="metaobject-address">`;
      html += `<span class="address-icon">📍</span>`;
      html += `<div class="address-text">`;
      
      // Show street address with identifier on first line
      if (streetField && streetField.value) {
        html += `${this.escapeHtml(streetField.value)}`;
        
        //CER 04302026 NOT SURE WHY THEY ARE DISPLAYING THE IDENTIFIER HERE (STATE) COMMENTED OUT
        // Add identifier after street if available
        //if (identifierField && identifierField.value) {
         // html += `, ${this.escapeHtml(identifierField.value)}`;
       // }
        
        html += `<br>`;
      }
      
      // Then show city, state, zip on second line
      if (addressField && addressField.value) {
        html += `${this.escapeHtml(addressField.value)}`;
      }
      
      html += `</div>`;
      html += `</div>`;
      html += `</div>`;
      html += `</div>`;
    }

    // Handle phone and link fields specially - put them on the same row
    const phoneField = metaobject.fields.find(f => f.key === 'phone');
    const linkField = metaobject.fields.find(f => f.key === 'link');
    
    if (phoneField || linkField) {
      html += `<div class="metaobject-field">`;
      html += `<div class="metaobject-actions-row">`;
      
      // Add phone if available
      if (phoneField && phoneField.value) {
        html += `<div class="metaobject-action-item">`;
        html += this.formatFieldValue(phoneField);
        html += `</div>`;
      }
      
      // Add link if available
      if (linkField && linkField.value) {
        html += `<div class="metaobject-action-item">`;
        html += this.formatFieldValue(linkField);
        html += `</div>`;
      }
      
      html += `</div>`;
      html += `</div>`;
    }

    // Render other fields with enhanced formatting, but skip the fields we've handled specially
    metaobject.fields.forEach(field => {
      // Skip fields we've already handled or will handle specially
      if (['name', 'address', 'street', 'identifier', 'phone', 'link', 'note', 'latitude', 'longitude'].includes(field.key)) return;
      
      // Skip empty fields
      if (!field.value) return;

      html += `<div class="metaobject-field">`;
      html += `<div class="metaobject-field-label">${this.formatFieldLabel(field.key)}:</div>`;
      html += `<div class="metaobject-field-value">${this.formatFieldValue(field)}</div>`;
      html += `</div>`;
    });

    // Handle note field specially - put it at the bottom
    const noteField = metaobject.fields.find(f => f.key === 'note');
    if (noteField && noteField.value) {
      html += `<div class="metaobject-field metaobject-note-field">`;
      html += `<div class="metaobject-field-label">${this.formatFieldLabel(noteField.key)}:</div>`;
      html += `<div class="metaobject-field-value">${this.formatFieldValue(noteField)}</div>`;
      html += `</div>`;
    }

    item.innerHTML = html;
    return item;
  }

  formatFieldLabel(key) {
    // Handle special case for identifier field
    if (key.toLowerCase() === 'identifier') {
      return 'State';
    }
    
    return key
      .replace(/_/g, ' ')
      .replace(/-/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  }

  formatFieldValue(field) {
    if (!field.value) return '';

    const value = field.value.trim();
    
    // Handle special "Link: URL" format
    if (value.toLowerCase().startsWith('link:')) {
      const url = value.substring(5).trim(); // Remove "Link:" and trim
      if (this.isValidUrl(url)) {
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="metaobject-link">
          Get Directions
        </a>`;
      }
    }

    // Handle direct URLs
    if (this.isValidUrl(value)) {
      return `<a href="${value}" target="_blank" rel="noopener noreferrer" class="metaobject-link">
        Get Directions
      </a>`;
    }

    // Handle email addresses
    if (this.isEmail(value)) {
      return `<a href="mailto:${value}" class="metaobject-email">
        <span class="email-icon">✉️</span> ${this.escapeHtml(value)}
      </a>`;
    }

    // Handle phone numbers
    if (this.isPhoneNumber(value)) {
      const cleanPhone = value.replace(/[^\d+()-]/g, '');
      return `<a href="tel:${cleanPhone}" class="metaobject-phone">
        ${this.escapeHtml(value)}
      </a>`;
    }

    // Handle hours/time fields
    if (['hours', 'time', 'open', 'close'].some(keyword => 
      field.key.toLowerCase().includes(keyword))) {
      return `<div class="metaobject-hours">
        <span class="hours-icon">🕒</span> ${this.escapeHtml(value)}
      </div>`;
    }

    // Default: just escape and return
    return this.escapeHtml(value);
  }

  isValidUrl(string) {
    try {
      // Check if it starts with http/https
      if (string.startsWith('http://') || string.startsWith('https://')) {
        new URL(string);
        return true;
      }
      // Also try with https prefix for cases like "www.example.com"
      if (string.startsWith('www.')) {
        new URL('https://' + string);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  isEmail(string) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(string);
  }

  isPhoneNumber(string) {
    // Match various phone number formats
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$|^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$|^\d{3}[-.\s]?\d{3}[-.\s]?\d{4}$/;
    const cleanString = string.replace(/[^\d+()-]/g, '');
    return phoneRegex.test(cleanString) && cleanString.length >= 10;
  }

  updateLoadingProgress(message) {
    const loadingEl = this.querySelector('.metaobjects-loading p');
    if (loadingEl) {
      loadingEl.textContent = message;
    }
  }

  initializeFiltersAndSearch() {
    this.createSearchAndFilterUI();
    this.attachEventListeners();
  }

  createSearchAndFilterUI() {
    const container = this.querySelector('.metaobjects-container');
    if (!container) return;

    // Create search and filter controls
    const controlsHTML = `
      <div class="metaobjects-controls">
        <div class="search-and-filter-row">
          <div class="search-section">
            <div class="general-search">
              <input 
                type="text" 
                placeholder="Search locations..." 
                class="metaobjects-search"
                value="${this.currentSearchTerm}"
              >
              <button class="search-clear" style="display: ${this.currentSearchTerm ? 'inline-block' : 'none'};">✕</button>
            </div>
          </div>
          
          <div class="filters-section">
            <div class="filter-group">
              <label>Filter by:</label>
              <div class="filter-controls">
                <!-- Filters will be populated dynamically based on available fields -->
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    // Insert controls before the grid
    const grid = container.querySelector('.metaobjects-grid');
    if (grid) {
      grid.insertAdjacentHTML('beforebegin', controlsHTML);
    }

    // Populate dynamic filters based on metaobject fields
    this.populateFilters();
  }

  populateFilters() {
    if (this.allMetaobjects.length === 0) return;

    const filterControls = this.querySelector('.filter-controls');
    if (!filterControls) return;

    // Analyze fields to create appropriate filters
    const fieldAnalysis = this.analyzeFields();
    let filtersHTML = '';

    // Create filters for fields that have at least 1 value
    Object.entries(fieldAnalysis).forEach(([fieldKey, analysis]) => {
      if (analysis.uniqueValues.size >= 1 && analysis.uniqueValues.size <= 60) {
        filtersHTML += this.createFilterControl(fieldKey, analysis);
      }
    });

    if (filtersHTML) {
      filterControls.innerHTML = filtersHTML;
    } else {
      filterControls.innerHTML = '<span class="no-filters">No filterable fields available</span>';
    }
  }

  analyzeFields() {
    const analysis = {};

    this.allMetaobjects.forEach(metaobject => {
      metaobject.fields.forEach(field => {
        if (!field.value || field.key === 'name') return;
        
        // Exclude certain fields from filters
        const excludedFields = ['note', 'notes', 'description', 'link', 'directions', 'address', 'street', 'phone', 'latitude', 'longitude', 'name'];
        if (excludedFields.some(excluded => field.key.toLowerCase() === excluded)) return;

        if (!analysis[field.key]) {
          analysis[field.key] = {
            uniqueValues: new Set(),
            count: 0
          };
        }

        analysis[field.key].uniqueValues.add(field.value);
        analysis[field.key].count++;
      });
    });

    return analysis;
  }

  createFilterControl(fieldKey, analysis) {
    const values = Array.from(analysis.uniqueValues).sort();
    const displayName = this.formatFieldLabel(fieldKey);
    
    // For the "All" option, use a more natural plural form
    const allOptionText = ['identifier', 'state'].includes(fieldKey.toLowerCase()) ? 'All States' : `All ${displayName}s`;

    return `
      <div class="filter-control">
        <select id="filter-${fieldKey}" data-field="${fieldKey}">
          <option value="">${allOptionText}</option>
          ${values.map(value => `
            <option value="${this.escapeHtml(value)}" ${this.activeFilters[fieldKey] === value ? 'selected' : ''}>
              ${this.escapeHtml(value)}
            </option>
          `).join('')}
        </select>
      </div>
    `;
  }

  attachEventListeners() {
    // Search functionality
    const searchInput = this.querySelector('.metaobjects-search');
    const searchClear = this.querySelector('.search-clear');

    if (searchInput) {
      searchInput.addEventListener('input', (e) => {
        this.currentSearchTerm = e.target.value.toLowerCase();
        this.applyFiltersAndSearch();
        
        if (searchClear) {
          searchClear.style.display = this.currentSearchTerm ? 'inline-block' : 'none';
        }
      });
    }

    if (searchClear) {
      searchClear.addEventListener('click', () => {
        this.currentSearchTerm = '';
        if (searchInput) searchInput.value = '';
        searchClear.style.display = 'none';
        this.applyFiltersAndSearch();
      });
    }

    // Filter functionality
    this.querySelectorAll('.filter-control select').forEach(select => {
      select.addEventListener('change', (e) => {
        const fieldKey = e.target.dataset.field;
        const value = e.target.value;
        
        if (value) {
          this.activeFilters[fieldKey] = value;
        } else {
          delete this.activeFilters[fieldKey];
        }
        
        this.applyFiltersAndSearch();
      });
    });
  }

  applyFiltersAndSearch() {
    let filtered = [...this.allMetaobjects];

    // Apply text search
    if (this.currentSearchTerm) {
      filtered = filtered.filter(metaobject => {
        const searchableText = [
          metaobject.field?.value || '',
          ...metaobject.fields.map(f => f.value || '')
        ].join(' ').toLowerCase();
        
        return searchableText.includes(this.currentSearchTerm);
      });
    }

    // Apply filters
    Object.entries(this.activeFilters).forEach(([fieldKey, filterValue]) => {
      filtered = filtered.filter(metaobject => {
        const field = metaobject.fields.find(f => f.key === fieldKey);
        return field && field.value === filterValue;
      });
    });

    this.filteredMetaobjects = filtered;
    this.renderMetaobjects(this.filteredMetaobjects);
  }

  showLoading() {
    const loadingEl = this.querySelector('.metaobjects-loading');
    const errorEl = this.querySelector('.metaobjects-error');
    const containerEl = this.querySelector('.metaobjects-grid');

    if (loadingEl) loadingEl.style.display = 'block';
    if (errorEl) errorEl.style.display = 'none';
    if (containerEl) containerEl.innerHTML = '';
  }

  showError(message) {
    const loadingEl = this.querySelector('.metaobjects-loading');
    const errorEl = this.querySelector('.metaobjects-error');
    const containerEl = this.querySelector('.metaobjects-grid');

    if (loadingEl) loadingEl.style.display = 'none';
    if (errorEl) errorEl.style.display = 'block';
    if (containerEl) containerEl.innerHTML = '';

    // Update error message
    if (errorEl) {
      const errorText = errorEl.querySelector('p');
      if (errorText) {
        errorText.textContent = message;
      }
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

customElements.define('metaobjects-list', MetaobjectsList);
